/**
 * The page's sockets (B3.5, B3.7): what the page needs of a WebSocket and of a clock, the wait
 * before the next try, and one socket's events as both links read them. Shared by the room's
 * link (`roomClient.ts`) and the lobby's (`front/serverLobby.ts`); the tests give fakes.
 */
export interface SocketLike {
  send(text: string): void;
  close(): void;
  addEventListener(
    type: "open" | "message" | "close" | "error",
    listener: (event: { readonly data?: unknown }) => void,
  ): void;
}

export interface LinkClock {
  readonly now: () => number;
  /** Runs `task` once after `ms`. */
  readonly after: (ms: number, task: () => void) => void;
}

/** The wait before try `attempt` (0 the first retry): past the list's end, its last wait. */
export function backoffMs(waits: readonly number[], attempt: number): number {
  return waits[Math.min(attempt, waits.length - 1)] ?? 0;
}

/**
 * One socket's events: it opened, a message (its text; undefined for data that is not text),
 * and its end, where a close and an error are alike.
 */
export function wireSocket(
  socket: SocketLike,
  on: {
    readonly open: () => void;
    readonly message: (text: string | undefined) => void;
    readonly gone: () => void;
  },
): void {
  socket.addEventListener("open", () => on.open());
  socket.addEventListener("message", (event) =>
    on.message(typeof event.data === "string" ? event.data : undefined),
  );
  socket.addEventListener("close", () => on.gone());
  socket.addEventListener("error", () => on.gone());
}
